import { Component, ElementRef, HostListener, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { PAGE_REGISTRY, PageEntry } from './page-registry';
import { SecureSessionService } from 'src/app/services/secure-session.service';
import { ImpersonationService } from 'src/app/user/teacher-impersonation/impersonation.service';

// Roles that count as a "real teacher" — i.e. row-level security will
// resolve to a teacher record so teacher pages actually load data.
// super_admin is intentionally NOT in this list; admins must impersonate.
const TEACHER_ROLES = ['teacher_acadamic_user', 'teacher_academic_admin', 'Teacher'];

@Component({
  selector: 'app-global-search',
  templateUrl: './global-search.component.html',
  styleUrls: ['./global-search.component.scss'],
})
export class GlobalSearchComponent implements OnDestroy {
  query = '';
  open = false;
  results: PageEntry[] = [];
  highlightIndex = 0;
  isEffectivelyTeacher = false;
  private userRoles: string[] = [];
  private impersonationSub?: Subscription;

  constructor(
    private router: Router,
    private session: SecureSessionService,
    private impersonation: ImpersonationService,
    private host: ElementRef,
  ) {
    try {
      const data = JSON.parse(this.session.getSession() || '[]');
      this.userRoles = (data?.[0]?.roles as string[]) || [];
    } catch { this.userRoles = []; }
    this.recomputeTeacherState();
    this.impersonationSub = this.impersonation.impersonationChanged$.subscribe(() => {
      this.recomputeTeacherState();
    });
  }

  ngOnDestroy(): void {
    this.impersonationSub?.unsubscribe();
  }

  onInput(): void {
    const q = this.query.trim().toLowerCase();
    if (!q) { this.results = []; this.open = false; return; }
    const allowed = (e: PageEntry) => !e.roles?.length
      || e.roles.some((r) => this.userRoles.includes(r));
    this.results = PAGE_REGISTRY
      .filter(allowed)
      .filter((e) =>
        e.label.toLowerCase().includes(q)
        || e.module.toLowerCase().includes(q)
      )
      .slice(0, 12);
    this.highlightIndex = 0;
    this.open = this.results.length > 0;
  }

  isDisabled(entry: PageEntry): boolean {
    return !!entry.requiresImpersonation && !this.isEffectivelyTeacher;
  }

  go(entry: PageEntry): void {
    if (this.isDisabled(entry)) return;
    this.router.navigateByUrl(entry.path);
    this.clear();
  }

  clear(): void {
    this.query = '';
    this.results = [];
    this.open = false;
  }

  onKeyDown(event: KeyboardEvent): void {
    if (!this.open || !this.results.length) return;
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      this.highlightIndex = (this.highlightIndex + 1) % this.results.length;
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      this.highlightIndex = (this.highlightIndex - 1 + this.results.length) % this.results.length;
    } else if (event.key === 'Enter') {
      event.preventDefault();
      this.go(this.results[this.highlightIndex]);
    } else if (event.key === 'Escape') {
      this.clear();
    }
  }

  @HostListener('document:click', ['$event'])
  onDocClick(event: MouseEvent): void {
    if (!this.host.nativeElement.contains(event.target)) this.open = false;
  }

  private recomputeTeacherState(): void {
    const hasTeacherRole = this.userRoles.some((r) => TEACHER_ROLES.includes(r));
    this.isEffectivelyTeacher = hasTeacherRole || this.impersonation.isImpersonating();
  }
}
